import { createContext, useContext } from "react";
import { User } from "../types/user";
import { Event } from "../types/event";
import { Period } from "../types/period";
import { WerklijstenData } from "../types/werklijst";
import { useUsers } from "./UserContext";
import { useEvents } from "./EventsContext";
import { useAvailability } from "./AvailabilityContext";
import { usePeriods } from "./PeriodContext";
import { useWerklijsten } from "./WerklijstenContext";

/**
 * @deprecated Use domain-specific contexts instead:
 * - useUsers() for currentUser, colleagues
 * - useEvents() for events, refreshEvents
 * - useAvailability() for availabilityData, loadAvailabilityForYear
 * - usePeriods() for periods, loadPeriodsForYear
 * - useWerklijsten() for werklijstenData
 * 
 * This context is kept for backward compatibility during migration.
 */
interface AppState {
  currentUser: User | null;
  colleagues: User[];
  events: Event[];
  availabilityData: Record<string, { am: boolean; pm: boolean }>;
  werklijstenData: WerklijstenData;
  periods: Period[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  loadAvailabilityForYear: (year: number) => Promise<void>;
  loadPeriodsForYear: (year: number) => Promise<void>;
}

const AppContext = createContext<AppState>({
  currentUser: null,
  colleagues: [],
  events: [],
  availabilityData: {},
  werklijstenData: {},
  periods: [],
  isLoading: true,
  error: null,
  refreshData: async () => {},
  loadAvailabilityForYear: async () => {},
  loadPeriodsForYear: async () => {},
});

/**
 * @deprecated Compatibility wrapper. Use domain-specific contexts directly.
 * This wraps domain contexts to support components not yet migrated.
 */
export function AppProvider({ children }: { children: React.ReactNode }) {
  // Delegate to domain contexts
  const { currentUser, colleagues, isLoadingUsers, error: userError, refreshUsers } = useUsers();
  const { events, isLoadingEvents, refreshEvents } = useEvents();
  const { availabilityData, isLoadingAvailability, loadAvailabilityForYear, refreshAvailability } = useAvailability();
  const { periods, isLoadingPeriods, loadPeriodsForYear, refreshPeriods } = usePeriods();
  const { werklijstenData, refreshWerklijsten } = useWerklijsten();

  // Aggregate async method to refresh all data
  const refreshData = async () => {
    await Promise.all([
      refreshUsers(),
      refreshEvents(),
      refreshAvailability(),
      refreshPeriods(),
      refreshWerklijsten(),
    ]);
  };

  const value = {
    currentUser,
    colleagues,
    events,
    availabilityData,
    werklijstenData,
    periods,
    isLoading: isLoadingUsers || isLoadingEvents || isLoadingAvailability || isLoadingPeriods,
    error: userError,
    refreshData,
    loadAvailabilityForYear,
    loadPeriodsForYear,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

/**
 * @deprecated Use domain-specific hooks instead:
 * - useUsers() for currentUser, colleagues
 * - useEvents() for events
 * - useAvailability() for availabilityData
 * - usePeriods() for periods
 * - useWerklijsten() for werklijstenData
 */
export const useApp = () => useContext(AppContext);